/**
 * PDP Reverse-Price Timer
 *
 * Counts down to `data-end-epoch` (Unix seconds, server-emitted in shop timezone),
 * formats as "HH hr : MM min : SS sec", swaps to "Aktion beendet" on zero, and
 * reloads the page after a 5-second hold so the new (reverted) price is shown.
 *
 * Visibility is gated server-side in `pdp-reverse-price-timer.liquid` (only
 * rendered when 0 < remaining <= 48h). This module assumes a valid future epoch
 * is present and exits silently otherwise.
 */

/** @param {number} n */
function pad2(n) {
  return String(n).padStart(2, '0');
}

/** @param {number} ms */
function formatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return `${pad2(hours)} hr : ${pad2(minutes)} min : ${pad2(seconds)} sec`;
}

/** @param {HTMLElement} root */
function initTimer(root) {
  const valueEl = root.querySelector('[data-pdp-reverse-timer-value]');
  const labelEl = root.querySelector('[data-pdp-reverse-timer-label]');
  const endedText = root.dataset.endedText || 'Aktion beendet';

  let endEpoch = Number(root.dataset.endEpoch || '0');

  // Fallback: parse raw datetime if epoch missing/invalid (defensive parity with Liquid)
  if ((!Number.isFinite(endEpoch) || endEpoch <= 0) && root.dataset.endDatetime) {
    const dateStr = String(root.dataset.endDatetime).trim();
    if (dateStr) {
      const parsed = new Date(dateStr.replace(' ', 'T'));
      if (!Number.isNaN(parsed.getTime())) {
        endEpoch = Math.floor(parsed.getTime() / 1000);
      }
    }
  }

  if (!Number.isFinite(endEpoch) || endEpoch <= 0) return;

  const endMs = endEpoch * 1000;
  let intervalId = 0;
  let reloadScheduled = false;

  const renderEnded = () => {
    if (labelEl) labelEl.hidden = true;
    if (valueEl) {
      valueEl.textContent = endedText;
    } else {
      root.textContent = endedText;
    }
    root.dataset.timerEnded = 'true';
  };

  const tick = () => {
    const remaining = endMs - Date.now();
    if (remaining <= 0) {
      renderEnded();
      window.clearInterval(intervalId);
      if (!reloadScheduled) {
        reloadScheduled = true;
        window.setTimeout(() => {
          window.location.reload();
        }, 5000);
      }
      return;
    }
    if (valueEl) {
      valueEl.textContent = formatRemaining(remaining);
    } else {
      root.textContent = `${root.dataset.label || ''} ${formatRemaining(remaining)}`.trim();
    }
  };

  tick();
  intervalId = window.setInterval(tick, 1000);
}

function init() {
  const roots = document.querySelectorAll('[data-pdp-reverse-timer]');
  roots.forEach((root) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.timerInitialized === 'true') return;
    root.dataset.timerInitialized = 'true';
    initTimer(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
